import { PictureAsPdf } from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';
import { ColumnHelper } from '@tanstack/react-table';
import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { LanguageCode } from '../../api/dto';
import { useApplicationContext } from '../application/ApplicationContextProvider';
import { PdfDialog } from '../PdfDialog';
import { BaseData, SearchPage, SearchPageProps } from './SearchPage';

type IdType = { id: number } | { masterId: number; versionNumber: number };

interface SearchPageWithPdfPreviewProps<TData extends BaseData, Id extends IdType> extends SearchPageProps<TData> {
    columnHelper: ColumnHelper<TData>;
    idGetter: (data: TData) => Id;
    fetchPdf: (parameters: Id & { langCode: string }) => Promise<Blob>;
}

export const SearchPageWithPdfPreview = <TData extends BaseData, Id extends IdType>({
    columnHelper,
    idGetter,
    fetchPdf,
    ...searchPageProps
}: SearchPageWithPdfPreviewProps<TData, Id>) => {
    const intl = useIntl();
    const { preferredLanguage } = useApplicationContext();
    const preferredLanguageCode = preferredLanguage?.code ?? LanguageCode.ENGLISH;

    const [selectedId, setSelectedId] = useState<Id | null>(null);

    const handleOpenPdfDialog = useCallback((id: Id) => setSelectedId(id), [setSelectedId]);
    const handleClosePdfDialog = useCallback(() => setSelectedId(null), [setSelectedId]);

    const columns = useMemo(
        () => [
            ...searchPageProps.columns,
            columnHelper.accessor((row) => row.id ?? '', {
                id: 'pdf',
                header: undefined,
                size: 25,
                enableSorting: false,
                cell: ({ row: { original } }) => (
                    <Tooltip title={intl.formatMessage({ id: 'document.action.preview' })} placement="top">
                        <IconButton edge="end" onClick={() => handleOpenPdfDialog(idGetter(original))} size="small">
                            <PictureAsPdf />
                        </IconButton>
                    </Tooltip>
                ),
                meta: {
                    initiallyHidden: true,
                    label: intl.formatMessage({ id: 'document.action.preview' }),
                },
            }),
        ],
        [searchPageProps.columns, columnHelper, handleOpenPdfDialog, intl]
    );

    return (
        <>
            <SearchPage {...searchPageProps} columns={columns} />
            {!!selectedId && (
                <PdfDialog
                    queryKey={[
                        'pdf-search',
                        searchPageProps.pageKey,
                        'id' in selectedId ? selectedId.id : '',
                        'masterId' in selectedId ? selectedId.masterId : '',
                        'versionNumber' in selectedId ? selectedId.versionNumber : '',
                    ]}
                    fetchPdf={() =>
                        fetchPdf({
                            ...({
                                id: 'id' in selectedId ? selectedId.id : undefined,
                                masterId: 'masterId' in selectedId ? selectedId.masterId : undefined,
                                versionNumber: 'versionNumber' in selectedId ? selectedId.versionNumber : undefined,
                            } satisfies Record<keyof IdType, number | undefined> as any as Id),
                            langCode: preferredLanguageCode,
                        })
                    }
                    open={true}
                    onClose={handleClosePdfDialog}
                />
            )}
        </>
    );
};
